import { SDK, ApiError } from "@ringcentral/sdk";
import Platform from "@ringcentral/sdk/lib/platform/Platform";
import FormData from "form-data";
import { createHash } from "node:crypto";

import { GroupIds } from "@/configuration/group-ids";
import { formatPhoneNumber } from "@/shared/utils/phone-utils";
import { logAndThrow, logger } from "@/src/lib/logging/logger";
import { unredacted } from "@/src/lib/logging/redact";
import { RC_INTERNAL_VH_FAX_NUMBER } from "@/src/lib/string";
import { CustomerIds } from "@/src/models/customer";
import { Fax, FaxDirection } from "@/src/models/fax";
import {
  ringCentralRoutingContextSchema,
  ringCentralServiceUsersSchema,
  type RingCentralRoutingContext,
  type RingCentralServiceUser,
} from "@/src/models/ringcentral-service-user";
import {
  getRingCentralAuthStorageBySessionKey,
  upsertRingCentralAuthStorage,
} from "@/src/repo/ringcentral-auth-storage";

export const RC_ENTP_CLINIC_FAX_NUMBER = "+18007096728";
export const RC_ENTP_SLEEP_FAX_NUMBER = "+18559746500";
export const RC_ENTP_SCI_FAX_NUMBER = "+18666174977";
export const RC_ENTP_SUBOTO_FAX_NUMBER = "+18333200097";
export const RC_ENTP_ENTS_FAX_NUMBER = "+18557945030";
export const RC_SIENT_FAX_NUMBER = "+18125425384";
export const RC_PINNACLE_FAX_NUMBER = "+17752602601";
export const RC_NIMBUS_FAX_NUMBER = "+14787440906";
export const RC_AUTONOMY_FAX_NUMBER = "+12148273690";
export const RC_AUTONOMY_EXTENSION_ID = "2551732014";
export const RC_CONFLUENT_FAX_NUMBER = "+17604572029";
export const RC_PACESETTER_PA_FAX_NUMBER = "+17262649267";
export const RC_PACESETTER_KY_FAX_NUMBER = "+15022338644";
export const RC_PACESETTER_KY_FAX_NUMBER_2 = "+15025767503";
export const RC_NVISION__EWEI_TORRANCE_FAX_NUMBER = "+13109612032";
export const RC_PACESETTER_CO_FAX_NUMBER = "+13038055157";
export const RC_BALANCE_HEALTH_FAX_NUMBER = "+18014786239";
export const RC_BALANCE_HEALTH_FAX_NUMBER_2 = "+18586666839";
export const RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER = "+19495227039";
export const RC_BALANCE_HEALTH_DESERT_FAX_NUMBER = "+14809442817";
export const RC_BALANCE_HEALTH_IFAC_FAX_NUMBER = "+14253201747";
export const RC_BALANCE_HEALTH_WFAI_FAX_NUMBER = "+18477379586";
export const RC_BALANCE_HEALTH_FCC_FAX_NUMBER = "+14062067833";
export const RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER = "+13033735069";
export const RC_BALANCE_HEALTH_1F2F_FAX_NUMBER = "+19482081196";
export const RC_BALANCE_HEALTH_FASWM_FAX_NUMBER = "+16163178759";
export const RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER = "+17023024819";
export const RC_BALANCE_HEALTH_UFAI_FAX_NUMBER = "+18053248256";
export const RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER = "+14242728829";
export const RC_BALANCE_HEALTH_BAFC_FAX_NUMBER = "+14153766746";
export const RC_BALANCE_HEALTH_SFAC_FAX_NUMBER = "+19167602346";
export const RC_BALANCE_HEALTH_PPO_FAX_NUMBER = "+19167605137";
export const RC_BALANCE_HEALTH_VPG_FAX_NUMBER = "+12093952643";

export const RC_BALANCE_HEALTH_PORTED_FAX_NUMBER_TO_EXISTING_FAX_NUMBER: Readonly<Record<string, string>> = {
  "+18336001541": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336001538": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339733739": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18333741418": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334493739": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556171543": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18335992495": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336380232": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18335650426": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18337603932": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339725269": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339735219": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336001537": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339735352": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334490753": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556171544": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334385675": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556211883": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334643590": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339735353": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334385671": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336001535": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18337412639": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18337646156": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556825351": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339733740": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336001531": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336152399": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334642415": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334505918": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336001543": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334714945": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18339733959": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334714131": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556171547": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18333912173": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18333741417": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556171595": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18336060181": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556211890": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18556212004": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334494365": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334643543": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334512201": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334500791": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334500792": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18334643542": RC_BALANCE_HEALTH_BAFC_FAX_NUMBER,
  "+18552479802": RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  "+18339052580": RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  "+18334494007": RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  "+18339052581": RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  "+18339740955": RC_BALANCE_HEALTH_PPO_FAX_NUMBER,
  "+18334412278": RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  "+18556177438": RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  "+18556177710": RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  "+18335812409": RC_BALANCE_HEALTH_SFAC_FAX_NUMBER,
  "+18339732757": RC_BALANCE_HEALTH_VPG_FAX_NUMBER,
  "+18334715462": RC_BALANCE_HEALTH_VPG_FAX_NUMBER,
  "+18018028585": RC_BALANCE_HEALTH_FAX_NUMBER,
  "+18339744744": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18338070124": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18334500207": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18337603906": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18446704653": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18446704693": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18338070125": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18339735218": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18334381472": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18334284525": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+18339735347": RC_BALANCE_HEALTH_FAX_NUMBER_2,
  "+19492899805": RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER,
  "+18335932772": RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER,
  "+18335932770": RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER,
  "+18336512522": RC_BALANCE_HEALTH_DESERT_FAX_NUMBER,
  "+18334505276": RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  "+18337643251": RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  "+18337645065": RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  "+18338030112": RC_BALANCE_HEALTH_IFAC_FAX_NUMBER,
  "+18474188950": RC_BALANCE_HEALTH_WFAI_FAX_NUMBER,
  "+14062858399": RC_BALANCE_HEALTH_FCC_FAX_NUMBER,
  "+19702303627": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+19702303761": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+13033531780": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+13035867775": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+17203067217": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+17203067229": RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER,
  "+17575252556": RC_BALANCE_HEALTH_1F2F_FAX_NUMBER,
  "+16167238019": RC_BALANCE_HEALTH_FASWM_FAX_NUMBER,
  "+17024106668": RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER,
  "+13103637619": RC_BALANCE_HEALTH_UFAI_FAX_NUMBER,
};

export function getBalanceHealthExistingFaxNumber(faxNumber: string): string {
  return RC_BALANCE_HEALTH_PORTED_FAX_NUMBER_TO_EXISTING_FAX_NUMBER[faxNumber] ?? faxNumber;
}

export const RC_PA_EAST__EAST_POMPANO_FAX_NUMBER = "+19547850755";
export const RC_PA_EAST__EAST_POMPANO_FAX_NUMBER_2 = "+19547081074";
export const RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER = "+19549713427";
export const RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_2 = "+19546887318";
export const RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_3 = "+17542419574";
export const RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_4 = "+19549714252";
export const RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER = "+18504826550";
export const RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_2 = "+18502669079";
export const RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_3 = "+18505265437";
export const RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER = "+19544936726";
export const RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER_2 = "+19548241782";
export const RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER = "+18133151625";
export const RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_2 = "+18136899557";
export const RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_3 = "+18137251685";
export const RC_PA_EAST__VOLUSIA_FAX_NUMBER = "+13864249130";
export const RC_PA_EAST__EDGEWOOD_FAX_NUMBER = "+18636666089";
export const RC_PA_EAST__EDGEWOOD_FAX_NUMBER_2 = "+18633163847";
export const RC_PA_EAST__ARLINGTON_FAX_NUMBER = "+19046594715";
export const RC_PA_EAST__CALADIUM_FAX_NUMBER = "+18636999000";
export const RC_PA_EAST__CALADIUM_FAX_NUMBER_2 = "+18632727387";
export const RC_PA_EAST__CALADIUM_FAX_NUMBER_3 = "+18635941749";
export const RC_PA_EAST__CALADIUM_FAX_NUMBER_4 = "+18636990895";
export const RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER = "+13058271753";
export const RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER_2 = "+17862061726";
export const RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER = "+17728734878";
export const RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_2 = "+17723365313";
export const RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_3 = "+17728734585";
export const RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_4 = "+19044909805";
export const RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER = "+13055321164";
export const RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER_2 = "+17866852842";
export const RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER = "+15613647785";
export const RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_2 = "+15612821692";
export const RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_3 = "+15613360192";
export const RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER = "+19545220755";
export const RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER_2 = "+17543003587";
export const RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER = "+15616279574";
export const RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER_2 = "+15612830766";
export const RC_PA_EAST__ORMOND_FAX_NUMBER = "+13866732760";
export const RC_PA_EAST__ORMOND_FAX_NUMBER_2 = "+13863614499";
export const RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER = "+15617335004";
export const RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_2 = "+15613375330";
export const RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_3 = "+15617402415";
export const RC_PA_EAST__CITRUS_PARK_FAX_NUMBER = "+18133435022";
export const RC_PA_EAST__CITRUS_PARK_FAX_NUMBER_2 = "+18136926133";
export const RC_PA_EAST__BALDWIN_FAX_NUMBER = "+18505225925";
export const RC_PA_EAST__BALDWIN_FAX_NUMBER_2 = "+14484002282";
export const RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER = "+19042016350";
export const RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER_2 = "+18502360488";
export const RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER = "+19043797312";
export const RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_2 = "+19046448669";
export const RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_3 = "+19048955083";
export const RC_PA_EAST__MACCLENNY_FAX_NUMBER = "+19042598416";
export const RC_PA_EAST__MACCLENNY_FAX_NUMBER_2 = "+19042240510";
export const RC_PA_EAST__MACCLENNY_FAX_NUMBER_3 = "+19042595766";
export const RC_PA_EAST__MAIN_STREET_FAX_NUMBER = "+19043583067";
export const RC_PA_EAST__AVENTURA_FAX_NUMBER = "+13056821602";
export const RC_PA_EAST__AVENTURA_FAX_NUMBER_2 = "+17867962750";
export const RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER = "+13055328196";
export const RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER_2 = "+13054361564";
export const RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER = "+19544868983";
export const RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER_2 = "+19547851947";
export const RC_PA_EAST__PLANTATION_FAX_NUMBER = "+19544246003";
export const RC_PA_EAST__PLANTATION_FAX_NUMBER_2 = "+17547997956";
export const RC_PA_EAST__ROYAL_PALM_FAX_NUMBER = "+15617989419";
export const RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_2 = "+15612100599";
export const RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_3 = "+15614306340";
export const RC_PA_EAST__SUNRISE_FAX_NUMBER = "+19547414912";
export const RC_PA_EAST__SUNRISE_FAX_NUMBER_2 = "+19543413610";
export const RC_PA_EAST__SUNRISE_FAX_NUMBER_3 = "+19547414281";
export const RC_PA_EAST__WELLINGTON_FAX_NUMBER = "+15619675762";
export const RC_PA_EAST__WELLINGTON_FAX_NUMBER_2 = "+14072329252";
export const RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER = "+19549666614";
export const RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_2 = "+19549664224";
export const RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_3 = "+17547323458";
export const RC_PA_EAST__PEMBROKE_PINES_FAX_NUMBER = "+19544360449";
export const RC_PA_EAST__SKYLAKE_FAX_NUMBER = "+13059406167";
export const RC_PA_EAST__SKYLAKE_FAX_NUMBER_2 = "+17862692963";
export const RC_PA_EAST__SKYLAKE_FAX_NUMBER_3 = "+19549657390";
export const RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER = "+19046425154";
export const RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER_2 = "+19044259112";
export const RC_PA_EAST__WESTON_FAX_NUMBER = "+19543898726";
export const RC_PA_EAST__WESTON_FAX_NUMBER_2 = "+19543376116";
export const RC_PA_EAST__WESTON_FAX_NUMBER_3 = "+17542891017";
export const RC_PA_EAST__BOCA_RATON_FAX_NUMBER = "+15614777707";
export const RC_PA_EAST__BOCA_RATON_FAX_NUMBER_2 = "+19548400850";
export const RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER = "+19543453474";
export const RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER_2 = "+19547566234";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER = "+19544502833";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_2 = "+15617332068";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_3 = "+17543150509";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_4 = "+17543367992";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_5 = "+17868472236";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_6 = "+19043542304";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_7 = "+19045395510";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_8 = "+19544502819";
export const RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_9 = "+19549657405";
export const RC_PA_EAST__CAUSEWAY_FAX_NUMBER = "+13059812700";
export const RC_PA_EAST__CAUSEWAY_FAX_NUMBER_2 = "+17862880681";
export const RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER = "+18632933732";
export const RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_2 = "+18632591508";
export const RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_3 = "+18632794644";
export const RC_PA_EAST__HIALEAH_FAX_NUMBER = "+13057790729";
export const RC_PA_EAST__RIVERVIEW_FAX_NUMBER = "+18136548129";
export const RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER = "+18636832579";
export const RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_2 = "+18134714453";
export const RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_3 = "+18632260221";
export const RC_PA_EAST__BRICKELL_FAX_NUMBER = "+13056863920";
export const RC_PA_EAST__BRICKELL_FAX_NUMBER_2 = "+13055096413";
export const RC_PA_EAST__BRICKELL_FAX_NUMBER_3 = "+13058230914";
export const RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER = "+15618636627";
export const RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_2 = "+15615768551";
export const RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_3 = "+15616972881";
export const RC_PA_EAST__TRINITY_FAX_NUMBER = "+17273768824";
export const RC_PA_EAST__TRINITY_FAX_NUMBER_2 = "+17272015298";
export const RC_PA_EAST__TRINITY_FAX_NUMBER_3 = "+17277293095";
export const RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER = "+19549663616";
export const RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_2 = "+17543018490";
export const RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_3 = "+19549897917";
export const RC_PA_EAST__SUNCOAST_LARGO_FAX_NUMBER = "+17275866278";
export const RC_PA_EAST__NORTHSIDE_DUNN_AVE_FAX_NUMBER = "+19046594650";
export const RC_PA_EAST__CLEARWATER_FAX_NUMBER = "+17274490594";
export const RC_PA_EAST__CLEARWATER_FAX_NUMBER_2 = "+18138181540";
export const RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER = "+13867387497";
export const RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER_2 = "+13867341824";
export const RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER = "+18505887673";
export const RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER_2 = "+18507674777";
export const RC_PA_EAST__HOMESTEAD_FAX_NUMBER = "+13052455950";
export const RC_PA_EAST__HOMESTEAD_FAX_NUMBER_2 = "+17862982668";
export const RC_PA_EAST__HOMESTEAD_FAX_NUMBER_3 = "+19549657366";
export const RC_PA_EAST__HOMESTEAD_PAVILION_FAX_NUMBER = "+17864462025";
export const RC_PA_EAST__WEST_KENDALL_FAX_NUMBER = "+13053822708";
export const RC_PA_EAST__WEST_KENDALL_FAX_NUMBER_2 = "+17862692994";
export const RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER = "+13052329082";
export const RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_2 = "+13057779180";
export const RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_3 = "+17862202105";
export const RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_4 = "+17863426061";
export const RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_5 = "+17863581190";
export const RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER = "+13863253742";
export const RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER_2 = "+19045877501";
export const RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER = "+13212678487";
export const RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER_2 = "+13212744036";
export const RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER = "+13523359427";
export const RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_2 = "+13523358202";
export const RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_3 = "+13523358888";
export const RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER = "+13862554927";
export const RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_2 = "+13862554922";
export const RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_3 = "+13863230550";
export const RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_4 = "+19042240513";
export const RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER = "+19043960128";
export const RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_2 = "+19042240506";
export const RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_3 = "+19043963694";
export const RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_4 = "+19043963964";
export const RC_PA_EAST__LAKE_MARY_FAX_NUMBER = "+14073301140";
export const RC_PA_EAST__LAKE_MARY_FAX_NUMBER_2 = "+16892042075";
export const RC_PA_EAST__LAKE_MARY_FAX_NUMBER_3 = "+16892042180";
export const RC_PA_EAST__LAKE_MARY_FAX_NUMBER_4 = "+16892042183";
export const RC_PA_EAST__LAKE_MARY_FAX_NUMBER_5 = "+16894443156";
export const RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER = "+13216388661";
export const RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER_2 = "+13213291161";
export const RC_PA_EAST__A_PLUS_FAX_NUMBER = "+13524046955";
export const RC_PA_EAST__ALAFAYA_1755_FAX_NUMBER = "+14072491755";
export const RC_PA_EAST__ALTAMONTE_FAX_NUMBER = "+14078311068";
export const RC_PA_EAST__CONWAY_FAX_NUMBER = "+14072811916";
export const RC_PA_EAST__RED_BUG_FAX_NUMBER = "+14073660025";
export const RC_PA_EAST__FERNANDINA_BEACH_FAX_NUMBER = "+19044913173";
export const RC_PA_EAST__RIVER_CITY_FAX_NUMBER = "+19044820985";
export const RC_PA_EAST__SAXON_FAX_NUMBER = "+13868510664";
export const RC_PA_EAST__SOUTH_LAKE_FAX_NUMBER = "+13522420053";
export const RC_PA_CENTRAL__BOERNE_FAX_NUMBER = "+18308165056";
export const RC_PA_CENTRAL__BOERNE_FAX_NUMBER_2 = "+18774091489";
export const RC_PA_CENTRAL__BELVERDE_FAX_NUMBER = "+18304384050";
export const RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_2 = "+19037674195";
export const RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_3 = "+12105797296";
export const RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_4 = "+18774092115";
export const RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER = "+12105664779";
export const RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER_2 = "+18774091710";
export const RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER = "+12104942631";
export const RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER_2 = "+18884983111";
export const RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER = "+12104799294";
export const RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_2 = "+13463532720";
export const RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_3 = "+12104920977";
export const RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_4 = "+18774092887";
export const RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER = "+12105684862";
export const RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_2 = "+18303587711";
export const RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_3 = "+18306090572";
export const RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_4 = "+18776530582";
export const RC_STRIDECARE_1A_FAX_NUMBER = "+18669464085";
export const RC_STRIDECARE_1B_FAX_NUMBER = "+12143823201";
export const RC_STRIDECARE_2_FAX_NUMBER = "+12815650568";
export const RC_STRIDECARE_3_FAX_NUMBER = "+15125511651";
export const RC_STRIDECARE_4_FAX_NUMBER = "+12105044303";
export const RC_STRIDECARE_5_FAX_NUMBER = "+14697164193";
export const RC_CVVC_FAX_NUMBER = "+18435739660";
export const RC_CVVC_2A_FAX_NUMBER = "+18435778868";
export const RC_CVVC_2B_FAX_NUMBER = "+18437642890";
export const RC_ENTP_MENT_FAX_NUMBER = "+14107931170";
export const RC_COUG_20015_FAX_NUMBER = "+16143962465";
export const RC_COUG_20016_FAX_NUMBER = "+16143962480";
export const RC_COUG_20022_FAX_NUMBER = "+16143962580";
export const RC_COUG_20025_FAX_NUMBER = "+16143962641";
export const RC_COUG_20029_FAX_NUMBER = "+16147291237";
export const RC_COUG_20014_FAX_NUMBER = "+16147297702";
export const RC_COUG_20012_FAX_NUMBER = "+16147960370";
export const RC_COUG_20011_FAX_NUMBER = "+16147962901";
export const RC_COUG_20026_FAX_NUMBER = "+16149444771";
export const RC_COUG_20018_FAX_NUMBER = "+16149445069";
export const RC_COUG_20010_FAX_NUMBER = "+16149445071";
export const RC_COUG_20020_FAX_NUMBER = "+16149445073";
export const RC_COUG_20040_FAX_NUMBER = "+16149445074";
export const RC_COUG_20009_FAX_NUMBER = "+16149445075";
export const RC_COUG_20016_2_FAX_NUMBER = "+16143962481";
export const RC_COUG_20041_FAX_NUMBER = "+16142124817";
export const RC_INSIGHT_FAX_NUMBER = "+15594322632";
export const RC_TRICITY_FAX_NUMBER = "+15097835438";

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_BASE_DELAY = 1000; // milliseconds

function computeRingCentralSessionKey(credentials: { clientId: string; jwt: string }): string {
  return createHash("sha256").update(`${credentials.clientId}\n${credentials.jwt}`, "utf8").digest("hex");
}

async function createPersistentRingCentralStorage(sessionKey: string): Promise<{
  storage: Storage;
  flush: () => Promise<void>;
}> {
  const loaded = await getRingCentralAuthStorageBySessionKey(sessionKey);
  const data: Record<string, string> = loaded?.entries ? { ...loaded.entries } : {};
  let mutated = false;

  const storage: Storage = {
    get length() {
      return Object.keys(data).length;
    },
    key(index: number): string | null {
      const keys = Object.keys(data);

      return keys[index] ?? null;
    },
    getItem(key: string): string | null {
      return data[key] ?? null;
    },
    setItem(key: string, value: string): void {
      data[key] = value;
      mutated = true;
    },
    removeItem(key: string): void {
      delete data[key];
      mutated = true;
    },
    clear(): void {
      for (const k of Object.keys(data)) {
        delete data[k];
      }

      mutated = true;
    },
  };

  const flush = async (): Promise<void> => {
    if (!mutated) return;

    try {
      await upsertRingCentralAuthStorage(sessionKey, { ...data });
      mutated = false;
    } catch (error) {
      logger.warn("Flush RingCentral auth storage failed", {
        sessionKeyPrefix: sessionKey.slice(0, 8),
        error,
      });
    }
  };

  return { storage, flush };
}

export enum RingCentralMessageFaxMessageStatus {
  QUEUED = "Queued",
  SENT = "Sent",
  DELIVERED = "Delivered",
  DELIVERY_FAILED = "DeliveryFailed",
  SENDING_FAILED = "SendingFailed",
  RECEIVED = "Received",
  FAILED = "Failed",
  RETRIED = "Retried",
}

export enum RingCentralMessageReadStatus {
  READ = "Read",
  UNREAD = "Unread",
}

export enum RingCentralMessageStoreReadFilter {
  ALL = "",
  READ = "Read",
  UNREAD = "Unread",
}

enum RingCentralMessagePriority {
  NORMAL = "Normal",
  HIGH = "High",
}

export enum RingCentralMessageDirection {
  INBOUND = "Inbound",
  OUTBOUND = "Outbound",
}

enum RingCentralMessageFaxResolution {
  HIGH = "High",
  LOW = "Low",
}

export interface RingCentralMessage {
  id: string;
  uri?: string;
  type?: string;
  messageStatus?: RingCentralMessageFaxMessageStatus;
  faxErrorCode?: string;
  from?: {
    phoneNumber?: string;
    name?: string;
  };
  to?: {
    phoneNumber?: string;
    name?: string;
    faxErrorCode?: string;
  }[];
  creationTime?: string;
  lastModifiedTime?: string;
  readStatus?: RingCentralMessageReadStatus;
  priority?: RingCentralMessagePriority;
  attachments?: {
    id: string;
    uri: string;
    contentType: string;
    size?: number;
    filename?: string;
  }[];
  direction?: RingCentralMessageDirection;
  availability?: string;
  subject?: string;
  messageType?: string;
  faxResolution?: RingCentralMessageFaxResolution;
  faxPageCount?: number;
}

export type RingCentralCredentials = {
  clientId: string;
  clientSecret: string;
  jwt: string;
};

type RingCentralCredentialsWithCustomer = RingCentralCredentials & {
  customerIds: string[];
  extensionId?: string;
};

export const OFFICIAL_VH_APP_CLIENT_CREDENTIALS = {
  clientId: process.env.RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP!,
  clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_OFFICIAL_VH_APP!,
};

export function officialVhAppCredentials(jwt: string): RingCentralCredentials {
  return { ...OFFICIAL_VH_APP_CLIENT_CREDENTIALS, jwt };
}

interface ConfiguredRingCentralServiceUser extends RingCentralServiceUser {
  jwt: string;
}

// Empty until PA provides the service users and mailbox assignments; JWTs use named env vars like the legacy map.
const RINGCENTRAL_SERVICE_USERS: ConfiguredRingCentralServiceUser[] = [];

export function getRingCentralServiceUsers(): RingCentralServiceUser[] {
  return ringCentralServiceUsersSchema.parse(RINGCENTRAL_SERVICE_USERS);
}

export function getRingCentralServiceUsersForCustomer(customerId: string): RingCentralServiceUser[] {
  if (!RINGCENTRAL_SERVICE_USERS.some((user) => user.customerId === customerId)) return [];

  return getRingCentralServiceUsers().filter((user) => user.customerId === customerId);
}

export function getRingCentralServiceUserForFaxNumber(
  customerId: string,
  faxNumber: string | null | undefined
): RingCentralServiceUser | null {
  if (!faxNumber) return null;

  const users = getRingCentralServiceUsersForCustomer(customerId);

  if (users.length === 0) return null;

  const normalizedNumber = formatPhoneNumber(faxNumber);

  return (
    users.find((user) =>
      user.mailboxes.some((mailbox) => mailbox.mode === "ingest" && mailbox.faxNumbers.includes(normalizedNumber))
    ) ?? null
  );
}

export function getRingCentralServiceUserForOutboundFaxNumber(
  customerId: string,
  faxNumber: string | null | undefined
): RingCentralServiceUser | null {
  if (!faxNumber) return null;

  const users = getRingCentralServiceUsersForCustomer(customerId);

  if (users.length === 0) return null;

  const normalizedNumber = formatPhoneNumber(faxNumber);

  return users.find((user) => user.outboundFaxNumber === normalizedNumber) ?? null;
}

function getConfiguredRingCentralServiceUserForExtension(customerId: string, extensionId: string) {
  ringCentralServiceUsersSchema.parse(RINGCENTRAL_SERVICE_USERS);

  return (
    RINGCENTRAL_SERVICE_USERS.find(
      (user) =>
        user.customerId === customerId &&
        (user.extensionId === extensionId || user.mailboxes.some((mailbox) => mailbox.extensionId === extensionId))
    ) ?? null
  );
}

export function getRingCentralServiceUserForExtension(
  customerId: string,
  extensionId: string
): RingCentralServiceUser | null {
  const configuredUser = getConfiguredRingCentralServiceUserForExtension(customerId, extensionId);

  if (!configuredUser) return null;

  const { jwt: _jwt, ...user } = configuredUser;

  return user;
}

/**
 * Every JWT maps to a customer RingCentral extension/user account which can have multiple fax numbers
 * clientId and clientSecret are preferred to be RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP the Valerie Health app
 * Otherwise we have a clientId and clientSecret generated per RingCentral extension/user account
 */
const RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER: Record<string, RingCentralCredentialsWithCustomer> = {
  [RC_CONFLUENT_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_CONFLUENT!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_CONFLUENT!,
    jwt: process.env.RINGCENTRAL_JWT_CONFLUENT!,
    customerIds: [CustomerIds.CONFLUENT_LIVE],
  },
  // AUTONOMY fax number
  [RC_AUTONOMY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_AUTONOMY!),
    customerIds: [CustomerIds.AUTONOMY_LIVE, CustomerIds.AUTONOMY_SANDBOX],
    extensionId: RC_AUTONOMY_EXTENSION_ID,
  },
  // NVISION (EWEI Torrance) — routed to the production customer and group
  [RC_NVISION__EWEI_TORRANCE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_NVISION!),
    customerIds: [CustomerIds.NVISION_LIVE],
  },
  // PACESETTER fax numbers
  [RC_PACESETTER_PA_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_OFFICIAL_VH_APP!,
    jwt: process.env.RINGCENTRAL_JWT_PACESETTER!,
    customerIds: [CustomerIds.PACESETTER_LIVE, CustomerIds.PACESETTER_SANDBOX],
  },
  [RC_PACESETTER_KY_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_OFFICIAL_VH_APP!,
    jwt: process.env.RINGCENTRAL_JWT_PACESETTER!,
    customerIds: [CustomerIds.PACESETTER_LIVE, CustomerIds.PACESETTER_SANDBOX],
  },
  [RC_PACESETTER_KY_FAX_NUMBER_2]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_OFFICIAL_VH_APP!,
    jwt: process.env.RINGCENTRAL_JWT_PACESETTER!,
    customerIds: [CustomerIds.PACESETTER_LIVE, CustomerIds.PACESETTER_SANDBOX],
  },
  [RC_PACESETTER_CO_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_OFFICIAL_VH_APP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_OFFICIAL_VH_APP!,
    jwt: process.env.RINGCENTRAL_JWT_PACESETTER!,
    customerIds: [CustomerIds.PACESETTER_LIVE, CustomerIds.PACESETTER_SANDBOX],
  },
  // BALANCE_HEALTH fax number
  [RC_BALANCE_HEALTH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_DESERT_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_IFAC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_WFAI_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_FCC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_1F2F_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_FASWM_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_UFAI_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_BAFC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_SFAC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_PPO_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  [RC_BALANCE_HEALTH_VPG_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_BALANCEHEALTH!),
    customerIds: [CustomerIds.BALANCE_HEALTH_LIVE, CustomerIds.BALANCE_HEALTH_SANDBOX],
  },
  // COUG_LIVE fax numbers
  [RC_COUG_20015_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20015!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20016_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20016!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20016_2_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20016!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20022_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20022!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20025_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20025!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20029_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20029!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20014_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20014!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20012_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20012!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20011_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20011!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20026_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20026!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20018_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20018!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20010_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20010!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20020_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20020!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20040_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20040!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20009_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20009!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  [RC_COUG_20041_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_COUG_20041!),
    customerIds: [CustomerIds.US_UROLOGY_PARTNERS_LIVE],
  },
  // ENTP fax numbers - all share the same credentials
  [RC_ENTP_CLINIC_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  [RC_ENTP_ENTS_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  [RC_ENTP_SLEEP_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP_SLEEP!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP_SLEEP!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP_SLEEP!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  [RC_ENTP_SCI_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP_SCI!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP_SCI!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP_SCI!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  [RC_ENTP_MENT_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP_MENT!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP_MENT!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP_MENT!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  [RC_ENTP_SUBOTO_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_ENTP_SUBOTO!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_ENTP_SUBOTO!,
    jwt: process.env.RINGCENTRAL_JWT_ENTP_SUBOTO!,
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  // SIENT fax number
  [RC_SIENT_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_ENTP!),
    customerIds: [CustomerIds.ENT_PARTNERS_LIVE],
  },
  // PINNACLE fax number
  [RC_PINNACLE_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_PINNACLE!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_PINNACLE!,
    jwt: process.env.RINGCENTRAL_JWT_PINNACLE!,
    customerIds: [CustomerIds.PINNACLE_MEDICAL_GROUP_LIVE],
  },
  // TRICITY (TriCity Orthopaedics) fax number
  [RC_TRICITY_FAX_NUMBER]: {
    clientId: process.env.RINGCENTRAL_CLIENT_ID_TRICITY!,
    clientSecret: process.env.RINGCENTRAL_CLIENT_SECRET_TRICITY!,
    jwt: process.env.RINGCENTRAL_JWT_TRICITY!,
    customerIds: [CustomerIds.TRICITY_LIVE],
  },
  // PA EAST REGION fax numbers
  [RC_PA_EAST__EAST_POMPANO_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EAST_POMPANO!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EAST_POMPANO_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EAST_POMPANO!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__COCONUT_CREEK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__COCONUT_CREEK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__COCONUT_CREEK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__COCONUT_CREEK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__NORTH_FLORIDA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__NORTH_FLORIDA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__NORTH_FLORIDA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__FORT_LAUDERDALE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__FORT_LAUDERDALE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BLOOMINGDALE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BLOOMINGDALE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BLOOMINGDALE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__VOLUSIA_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__VOLUSIA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EDGEWOOD_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EDGEWOOD!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EDGEWOOD_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EDGEWOOD!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ARLINGTON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ARLINGTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CALADIUM_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CALADIUM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CALADIUM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CALADIUM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CALADIUM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MIAMI_LAKES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MIAMI_LAKES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ST_LUCIE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ST_LUCIE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ST_LUCIE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ST_LUCIE_NORTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MIAMI_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MIAMI_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_BEACH_EAST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_BEACH_EAST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_BEACH_EAST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__FORT_LAUDERDALE_SOUTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__FORT_LAUDERDALE_SOUTH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALM_BEACH_GARDENS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALM_BEACH_GARDENS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ORMOND_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ORMOND!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ORMOND_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ORMOND!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_WEST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_WEST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOYNTON_WEST!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CITRUS_PARK_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CITRUS_PARK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CITRUS_PARK_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CITRUS_PARK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BALDWIN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BALDWIN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BALDWIN_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BALDWIN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BARTRAM_PARK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BARTRAM_PARK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EXCEL_PEDS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EXCEL_PEDS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EXCEL_PEDS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MACCLENNY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MACCLENNY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MACCLENNY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__MAIN_STREET_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__MAIN_STREET!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__AVENTURA_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__AVENTURA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__AVENTURA_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__AVENTURA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BLUE_LAGOON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BLUE_LAGOON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAUDERDALE_LAKES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAUDERDALE_LAKES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PLANTATION_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PLANTATION!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PLANTATION_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PLANTATION!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ROYAL_PALM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ROYAL_PALM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ROYAL_PALM!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SUNRISE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SUNRISE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SUNRISE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SUNRISE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SUNRISE_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SUNRISE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WELLINGTON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WELLINGTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WELLINGTON_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WELLINGTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOLLYWOOD_SHERIDAN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOLLYWOOD_SHERIDAN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOLLYWOOD_SHERIDAN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PEMBROKE_PINES_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PEMBROKE_PINES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SKYLAKE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SKYLAKE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SKYLAKE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__TOWN_CENTER_SOUTHSIDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__TOWN_CENTER_SOUTHSIDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WESTON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WESTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WESTON_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WESTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WESTON_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WESTON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOCA_RATON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOCA_RATON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BOCA_RATON_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BOCA_RATON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CORAL_SPRINGS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CORAL_SPRINGS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_5]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_6]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_7]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_8]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_9]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHAPEL_TRAIL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CAUSEWAY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CAUSEWAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CAUSEWAY_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CAUSEWAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WINTER_HAVEN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WINTER_HAVEN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WINTER_HAVEN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HIALEAH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HIALEAH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__RIVERVIEW_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__RIVERVIEW!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKELAND_HILLS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKELAND_HILLS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKELAND_HILLS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BRICKELL_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BRICKELL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BRICKELL_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BRICKELL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__BRICKELL_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__BRICKELL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_PALM_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_PALM_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_PALM_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__TRINITY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__TRINITY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__TRINITY_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__TRINITY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__TRINITY_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__TRINITY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EMERALD_WOODS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EMERALD_WOODS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__EMERALD_WOODS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SUNCOAST_LARGO_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SUNCOAST_LARGO!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__NORTHSIDE_DUNN_AVE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__NORTHSIDE_DUNN_AVE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CLEARWATER_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CLEARWATER!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CLEARWATER_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CLEARWATER!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_VOLUSIA_DELAND!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_VOLUSIA_DELAND!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PANAMA_CITY_DOWNTOWN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PANAMA_CITY_DOWNTOWN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOMESTEAD!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOMESTEAD!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOMESTEAD!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__HOMESTEAD_PAVILION_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__HOMESTEAD_PAVILION!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_KENDALL_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_KENDALL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__WEST_KENDALL_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__WEST_KENDALL!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALMETTO_BAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALMETTO_BAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALMETTO_BAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALMETTO_BAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_5]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__PALMETTO_BAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__KIDS_CARE_PALATKA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__KIDS_CARE_PALATKA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__N_BREV_TITUSVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__N_BREV_TITUSVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ALLIANCE_PEDIATRICS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ALLIANCE_PEDIATRICS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ALLIANCE_PEDIATRICS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_DAYTONA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_DAYTONA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_DAYTONA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_DAYTONA!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_JACKSONVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_JACKSONVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_JACKSONVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CHA_JACKSONVILLE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKE_MARY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKE_MARY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKE_MARY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKE_MARY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_5]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__LAKE_MARY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__N_BREV_PORT_ST_JOHN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__N_BREV_PORT_ST_JOHN!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__A_PLUS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__A_PLUS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ALAFAYA_1755_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ALAFAYA_1755!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__ALTAMONTE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__ALTAMONTE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__CONWAY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__CONWAY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__RED_BUG_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__RED_BUG!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__FERNANDINA_BEACH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__FERNANDINA_BEACH!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__RIVER_CITY_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__RIVER_CITY!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SAXON_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SAXON!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_EAST__SOUTH_LAKE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_EAST__SOUTH_LAKE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BOERNE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BOERNE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BOERNE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BOERNE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BELVERDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BELVERDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BELVERDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__BELVERDE!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__SCHERTZ!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__SCHERTZ!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__STONE_OAK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__STONE_OAK!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__CHURCHILL_ESTATES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__CHURCHILL_ESTATES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__CHURCHILL_ESTATES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__CHURCHILL_ESTATES!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__NBPA_COMMONS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_2]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__NBPA_COMMONS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_3]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__NBPA_COMMONS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_4]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_PA_CENTRAL__NBPA_COMMONS!),
    customerIds: [CustomerIds.PEDIATRIC_ASSOCIATES_LIVE, CustomerIds.PEDIATRIC_ASSOCIATES_SANDBOX],
  },
  // NIMBUS fax number
  [RC_NIMBUS_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_NIMBUS_MACON_OFFICIAL_VH_APP!),
    customerIds: [CustomerIds.NIMBUS_LIVE],
  },
  // STRIDECARE fax numbers
  [RC_STRIDECARE_1A_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_STRIDECARE_1B_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_STRIDECARE_2_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE_2!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_STRIDECARE_3_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE_3!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_STRIDECARE_4_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE_4!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_STRIDECARE_5_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_STRIDECARE_5!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_CVVC_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_CVVC!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_CVVC_2A_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_CVVC_2!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_CVVC_2B_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_CVVC_2!),
    customerIds: [CustomerIds.STRIDECARE_LIVE],
  },
  [RC_INTERNAL_VH_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_VH_INTERNAL!),
    customerIds: [],
  },
  // INSIGHT fax number
  [RC_INSIGHT_FAX_NUMBER]: {
    ...officialVhAppCredentials(process.env.RINGCENTRAL_JWT_INSIGHT!),
    customerIds: [CustomerIds.INSIGHT_LIVE, CustomerIds.INSIGHT_SANDBOX],
  },
};

export const FAX_NUMBER_TO_GROUP_ID: Record<string, GroupIds> = {
  [RC_ENTP_CLINIC_FAX_NUMBER]: GroupIds.ENT_PARTNERS_LIVE,
  [RC_ENTP_ENTS_FAX_NUMBER]: GroupIds.ENT_PARTNERS_ENTS_LIVE,
  [RC_ENTP_SLEEP_FAX_NUMBER]: GroupIds.ENT_PARTNERS_LIVE,
  [RC_ENTP_SCI_FAX_NUMBER]: GroupIds.ENT_PARTNERS_SCI_LIVE,
  [RC_ENTP_SUBOTO_FAX_NUMBER]: GroupIds.ENT_PARTNERS_SUBOTO_LIVE,
  [RC_SIENT_FAX_NUMBER]: GroupIds.SIENT_LIVE,
  [RC_ENTP_MENT_FAX_NUMBER]: GroupIds.ENT_PARTNERS_MENT_LIVE,
  [RC_PINNACLE_FAX_NUMBER]: GroupIds.PINNACLE_MEDICAL_GROUP_LIVE,
  [RC_TRICITY_FAX_NUMBER]: GroupIds.TRICITY_LIVE,
  [RC_NIMBUS_FAX_NUMBER]: GroupIds.NIMBUS_LIVE,
  [RC_COUG_20015_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20016_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20022_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20025_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20029_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20014_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20012_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20011_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20026_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20018_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20010_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20020_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20040_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20009_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20016_2_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_COUG_20041_FAX_NUMBER]: GroupIds.COUG_LIVE,
  [RC_AUTONOMY_FAX_NUMBER]: GroupIds.AUTONOMY_LIVE,
  [RC_PACESETTER_PA_FAX_NUMBER]: GroupIds.PACESETTER_LIVE,
  [RC_PACESETTER_KY_FAX_NUMBER]: GroupIds.PACESETTER_LIVE,
  [RC_PACESETTER_KY_FAX_NUMBER_2]: GroupIds.PACESETTER_LIVE,
  [RC_PACESETTER_CO_FAX_NUMBER]: GroupIds.PACESETTER_LIVE,
  [RC_NVISION__EWEI_TORRANCE_FAX_NUMBER]: GroupIds.NVISION_EWEI_TORRANCE_LIVE,
  [RC_BALANCE_HEALTH_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_FAX_NUMBER_2]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_ARTISAN_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_DESERT_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_IFAC_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_WFAI_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_FCC_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_FAC_ROCKIES_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_1F2F_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_FASWM_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_LVFAC_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_UFAI_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_PRECISION_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_BAFC_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_SFAC_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_PPO_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  [RC_BALANCE_HEALTH_VPG_FAX_NUMBER]: GroupIds.BALANCE_HEALTH_LIVE,
  ...Object.fromEntries(
    Object.keys(RC_BALANCE_HEALTH_PORTED_FAX_NUMBER_TO_EXISTING_FAX_NUMBER).map((faxNumber) => [
      faxNumber,
      GroupIds.BALANCE_HEALTH_LIVE,
    ])
  ),
  [RC_PA_EAST__EAST_POMPANO_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EAST_POMPANO_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__COCONUT_CREEK_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__NORTH_FLORIDA_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__FORT_LAUDERDALE_NORTH_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BLOOMINGDALE_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__VOLUSIA_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EDGEWOOD_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EDGEWOOD_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ARLINGTON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CALADIUM_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CALADIUM_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MIAMI_LAKES_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ST_LUCIE_NORTH_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MIAMI_BEACH_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_BEACH_EAST_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__FORT_LAUDERDALE_SOUTH_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALM_BEACH_GARDENS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ORMOND_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ORMOND_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOYNTON_WEST_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CITRUS_PARK_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CITRUS_PARK_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BALDWIN_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BALDWIN_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BARTRAM_PARK_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EXCEL_PEDS_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MACCLENNY_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__MAIN_STREET_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__AVENTURA_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__AVENTURA_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BLUE_LAGOON_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAUDERDALE_LAKES_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PLANTATION_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PLANTATION_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ROYAL_PALM_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SUNRISE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SUNRISE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SUNRISE_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WELLINGTON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WELLINGTON_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOLLYWOOD_SHERIDAN_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PEMBROKE_PINES_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SKYLAKE_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__TOWN_CENTER_SOUTHSIDE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WESTON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WESTON_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WESTON_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOCA_RATON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BOCA_RATON_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CORAL_SPRINGS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_5]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_6]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_7]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_8]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHAPEL_TRAIL_FAX_NUMBER_9]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CAUSEWAY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CAUSEWAY_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WINTER_HAVEN_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HIALEAH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__RIVERVIEW_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKELAND_HILLS_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BRICKELL_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BRICKELL_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__BRICKELL_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_PALM_BEACH_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__TRINITY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__TRINITY_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__TRINITY_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__EMERALD_WOODS_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SUNCOAST_LARGO_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__NORTHSIDE_DUNN_AVE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CLEARWATER_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CLEARWATER_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_VOLUSIA_DELAND_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PANAMA_CITY_DOWNTOWN_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOMESTEAD_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__HOMESTEAD_PAVILION_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_KENDALL_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__WEST_KENDALL_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__PALMETTO_BAY_FAX_NUMBER_5]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__KIDS_CARE_PALATKA_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__N_BREV_TITUSVILLE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ALLIANCE_PEDIATRICS_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_DAYTONA_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CHA_JACKSONVILLE_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_3]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_4]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__LAKE_MARY_FAX_NUMBER_5]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__N_BREV_PORT_ST_JOHN_FAX_NUMBER_2]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__A_PLUS_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ALAFAYA_1755_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__ALTAMONTE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__CONWAY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__RED_BUG_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__FERNANDINA_BEACH_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__RIVER_CITY_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SAXON_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_EAST__SOUTH_LAKE_FAX_NUMBER]: GroupIds.PA_EAST_REGION_LIVE,
  [RC_PA_CENTRAL__BOERNE_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__BOERNE_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_3]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__BELVERDE_FAX_NUMBER_4]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__SCHERTZ_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__STONE_OAK_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_3]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__CHURCHILL_ESTATES_FAX_NUMBER_4]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_2]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_3]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_PA_CENTRAL__NBPA_COMMONS_FAX_NUMBER_4]: GroupIds.PA_CENTRAL_REGION_LIVE,
  [RC_STRIDECARE_1A_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_STRIDECARE_1B_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_STRIDECARE_2_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_STRIDECARE_3_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_STRIDECARE_4_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_STRIDECARE_5_FAX_NUMBER]: GroupIds.STRIDECARE_LIVE,
  [RC_CVVC_FAX_NUMBER]: GroupIds.CVVC_LIVE,
  [RC_CVVC_2A_FAX_NUMBER]: GroupIds.CVVC_LIVE,
  [RC_CVVC_2B_FAX_NUMBER]: GroupIds.CVVC_LIVE,
  [RC_INSIGHT_FAX_NUMBER]: GroupIds.INSIGHT_LIVE,
};

export async function getGroupIdForFaxNumber(faxNumber: string): Promise<GroupIds | null> {
  return FAX_NUMBER_TO_GROUP_ID[faxNumber] ?? null;
}

/**
 * Picks the fax number whose RingCentral credentials send a group's outbound fax: the sender number,
 * else the group's first number, else the customer's first number, limited to the group's customer.
 */
export function resolveRcSendFaxNumber(
  group: { id: string; customerId: string },
  senderFaxNumber: string | null
): string | null {
  const candidates = [
    ...(senderFaxNumber ? [getBalanceHealthExistingFaxNumber(senderFaxNumber)] : []),
    ...getFaxNumbersForGroup(group.id),
    ...getFaxNumbersForCustomer(group.customerId, false),
  ];

  return (
    candidates.find((faxNumber) =>
      RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER[faxNumber]?.customerIds.includes(group.customerId)
    ) ?? null
  );
}

export function getFaxNumbersForGroup(groupId: string): string[] {
  const faxNumbers = Object.keys(FAX_NUMBER_TO_GROUP_ID)
    .filter((faxNumber) => FAX_NUMBER_TO_GROUP_ID[faxNumber] === groupId)
    .map(getBalanceHealthExistingFaxNumber);

  return [...new Set(faxNumbers)];
}

/**
 * Get all fax numbers configured for a customer (for multi-fax setups)
 * @param customerId - The customer ID to look up fax numbers for
 * @param dedupeByJwt - If true, deduplicates based on JWT (default: true). If multiple fax numbers share the same JWT, only one is returned.
 * @returns Array of fax number strings configured for the customer
 */
export function getFaxNumbersForCustomer(customerId: string, dedupeByJwt: boolean = true): string[] {
  const faxNumbers = Object.keys(RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER).filter((faxNumber) =>
    RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER[faxNumber]?.customerIds.includes(customerId)
  );

  if (!dedupeByJwt) {
    return faxNumbers;
  }

  const seenJwts = new Set<string>();

  return faxNumbers.filter((faxNumber) => {
    const credentials = RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER[faxNumber];

    if (credentials?.jwt && !seenJwts.has(credentials.jwt)) {
      seenJwts.add(credentials.jwt);

      return true;
    }

    return false;
  });
}

export function getCustomerJwtPollTargets(customerId: string): {
  faxNumbers: string[];
  missingJwtCount: number;
} {
  const allNumbers = getFaxNumbersForCustomer(customerId, false);
  const missingJwtCount = allNumbers.filter(
    (faxNumber) => !RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER[faxNumber]?.jwt
  ).length;

  return { faxNumbers: getFaxNumbersForCustomer(customerId, true), missingJwtCount };
}

/**
 * Extract the RingCentral fax number from an inbound fax.
 * For inbound faxes, the recipientList contains the RingCentral fax number (from message.subject).
 * @param fax - The fax object (must be an inbound fax)
 * @returns The RingCentral fax number, or null if not found
 */
export async function getFaxNumberFromInboundFax(fax: Fax): Promise<string | null> {
  if (fax.direction === FaxDirection.OUTBOUND) {
    return null;
  }

  if (!fax.recipientList) {
    return null;
  }

  // For inbound faxes, recipientList contains the RingCentral fax number
  // It may be comma-separated, so we take the first one
  const recipientList = fax.recipientList.split(",");
  const faxNumber = recipientList[0]?.trim();

  // Verify it's a valid fax number in our mapping
  if (faxNumber && (await getGroupIdForFaxNumber(faxNumber))) {
    return faxNumber;
  }

  return null;
}

const RINGCENTRAL_FAX_NOTIFY_SENDER = "notify@ringcentral.com";

export function parseSenderPhoneFromRingCentralFaxNotifyEmail(
  sender: string | null | undefined,
  subject: string | null | undefined
): string | null {
  if (!sender || !subject) {
    return null;
  }

  const normalizedSender = sender.trim().toLowerCase();
  const angleBracketMatch = normalizedSender.match(/<([^>]+)>/);
  const emailAddress = (angleBracketMatch?.[1] ?? normalizedSender).trim();

  if (emailAddress !== RINGCENTRAL_FAX_NOTIFY_SENDER) {
    return null;
  }

  const match = subject.match(/\((\d{3})\)\s*(\d{3})-(\d{4})/);

  return match ? `+1${match[1]}${match[2]}${match[3]}` : null;
}

export function rcExtensionPath(extensionId: string, suffix: string, accountId: string = "~"): string {
  return `/restapi/v1.0/account/${accountId}/extension/${extensionId}/${suffix}`;
}

function validateRingCentralCredentials(credentials: RingCentralCredentials, identifier: string) {
  if (!credentials.clientId) {
    const clientIdLength = credentials.clientId === undefined ? "undefined" : credentials.clientId.length;

    throw new Error(`RingCentral client ID not configured for ${identifier} (length: ${clientIdLength})`);
  }

  if (!credentials.clientSecret) {
    const clientSecretLength = credentials.clientSecret === undefined ? "undefined" : credentials.clientSecret.length;

    throw new Error(`RingCentral client secret not configured for ${identifier} (length: ${clientSecretLength})`);
  }

  if (!credentials.jwt) {
    const jwtLength = credentials.jwt === undefined ? "undefined" : credentials.jwt.length;

    throw new Error(`RingCentral JWT not configured for ${identifier} (length: ${jwtLength})`);
  }
}

function getRingCentralCredentials(routingContext: RingCentralRoutingContext): {
  credentials: RingCentralCredentials;
  extensionId: string;
  accountId: string;
  identifier: string;
} {
  const { customerId, faxNumber, extensionId } = ringCentralRoutingContextSchema.parse(routingContext);

  if (extensionId) {
    const serviceUser = getConfiguredRingCentralServiceUserForExtension(customerId, extensionId);

    if (!serviceUser) {
      logAndThrow("No RingCentral service user configured for extension", { customerId, extensionId });
    }

    return {
      credentials: officialVhAppCredentials(serviceUser.jwt),
      extensionId,
      accountId: serviceUser.accountId,
      identifier: `service user: ${serviceUser.id}`,
    };
  }

  if (!faxNumber) {
    throw new Error(
      `RingCentral credentials require a fax number. No fax number provided for customer ID ${customerId}`
    );
  }

  const faxCredentials = RINGCENTRAL_CREDENTIALS_BY_FAX_NUMBER[getBalanceHealthExistingFaxNumber(faxNumber)];

  if (!faxCredentials) {
    throw new Error(`RingCentral credentials not found for fax number ${faxNumber} (customer ID: ${customerId})`);
  }

  return {
    credentials: faxCredentials,
    extensionId: faxCredentials.extensionId ?? "~",
    accountId: "~",
    identifier: `fax number: ${faxNumber}`,
  };
}

export const RINGCENTRAL_SERVER_URL = "https://platform.ringcentral.com";

export function createRingCentralPlatform(
  options: Omit<NonNullable<ConstructorParameters<typeof SDK>[0]>, "server">
): Platform {
  return new SDK({ server: RINGCENTRAL_SERVER_URL, ...options }).platform();
}

async function rcClient(routingContext: RingCentralRoutingContext): Promise<{
  platform: Platform;
  flushStorage: () => Promise<void>;
  extensionId: string;
  accountId: string;
}> {
  const { customerId, faxNumber } = routingContext;
  const { credentials, identifier, extensionId, accountId } = getRingCentralCredentials(routingContext);

  validateRingCentralCredentials(credentials, identifier);

  const sessionKey = computeRingCentralSessionKey(credentials);
  const { storage, flush } = await createPersistentRingCentralStorage(sessionKey);

  const platform = createRingCentralPlatform({
    clientId: credentials.clientId,
    clientSecret: credentials.clientSecret,
    cachePrefix: `rc-${sessionKey}`,
    localStorage: storage,
  });

  let performedJwtLogin = false;

  try {
    await platform.ensureLoggedIn();
  } catch {
    performedJwtLogin = true;

    logger.info("Performing RC login", {
      customerId,
      faxNumber,
      sessionKeyPrefix: sessionKey.slice(0, 8),
      env: process.env.NODE_ENV,
    });

    try {
      await platform.login({ jwt: credentials.jwt });
    } catch (error) {
      logger.error("Failed to initialize RingCentral client:", {
        error,
        customerId,
        faxNumber,
      });
      throw error;
    }
  }

  await flush();

  if (!performedJwtLogin) {
    logger.info("Using existing RC tokens", {
      customerId,
      faxNumber,
      sessionKeyPrefix: sessionKey.slice(0, 8),
    });
  }

  return { platform, flushStorage: flush, extensionId, accountId };
}

async function withRcClient<T>(
  routingContext: RingCentralRoutingContext,
  fn: (platform: Platform, extensionId: string, accountId: string) => Promise<T>
): Promise<T> {
  let flushStorage: (() => Promise<void>) | null = null;
  const { customerId, faxNumber } = routingContext;

  try {
    let client = await rcClient(routingContext);

    flushStorage = client.flushStorage;

    try {
      return await fn(client.platform, client.extensionId, client.accountId);
    } catch (error) {
      if (error instanceof Error && "response" in error && (error as ApiError).response?.status === 401) {
        logger.warn("RC operation got 401 after SDK internal refresh failed, performing full re-login", {
          customerId,
          faxNumber,
        });

        client = await rcClient(routingContext);
        flushStorage = client.flushStorage;

        return await fn(client.platform, client.extensionId, client.accountId);
      }

      throw error;
    }
  } finally {
    if (flushStorage) {
      await flushStorage();
    }
  }
}

type AxiosLikeErrorResponse = {
  status?: number;
  headers?: { get?: (name: string) => string | null };
};

function getAxiosLikeResponse(error: unknown): AxiosLikeErrorResponse | undefined {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return undefined;
  }

  const res = (error as { response?: unknown }).response;

  if (typeof res !== "object" || res === null) {
    return undefined;
  }

  return res as AxiosLikeErrorResponse;
}

async function parseMessageStoreBatchGetResponse(resp: Response): Promise<RingCentralMessage[]> {
  const contentType = resp.headers.get("content-type") || "";

  if (contentType.includes("multipart/mixed")) {
    const results = await handleMultipartResponse(resp);

    return results.slice(1);
  }

  const jsonResponse = await resp.json();

  return [jsonResponse] as RingCentralMessage[];
}

async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxAttempts: number = DEFAULT_MAX_ATTEMPTS,
  baseDelay: number = DEFAULT_BASE_DELAY
): Promise<T> {
  //TODO: dsy Need rate limiting to be global on all request, not just in this context

  // This is the last error if all attempts are exhausted
  let lastError: Error = new Error("Operation failed after all attempts");

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await operation();

      return result;
    } catch (error: unknown) {
      lastError = error instanceof Error ? error : new Error(String(error));

      const response = getAxiosLikeResponse(error);

      if (response?.status === 429) {
        const retryAfter = response.headers?.get?.("Retry-After");
        const parsedRetryAfter = retryAfter ? parseInt(retryAfter, 10) : NaN;
        const delayMs = !isNaN(parsedRetryAfter) ? parsedRetryAfter * 1000 * 2 : baseDelay * Math.pow(2, attempt);

        if (maxAttempts > 1) {
          logger.info(`Rate limit hit, retrying after ${delayMs}ms (attempt ${attempt + 1}/${maxAttempts})`, {
            fileName: "ringcentral.ts",
            delayMs,
            attempt: attempt + 1,
            maxAttempts,
          });

          await new Promise((resolve) => setTimeout(resolve, delayMs));
        } else {
          throw new Error("Rate limit in effect. Attempt limit reached.");
        }

        continue;
      }

      // If it's not a rate limit error, only retry on 5xx server errors
      if (response?.status && response.status < 500) {
        throw error;
      }

      const delayMs = baseDelay * Math.pow(2, attempt);

      logger.info(`Operation failed, retrying after ${delayMs}ms (attempt ${attempt + 1}/${maxAttempts})`, {
        fileName: "ringcentral.ts",
        delayMs,
        attempt: attempt + 1,
        maxAttempts,
      });

      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  throw lastError;
}

export async function sendFaxRC(
  recipients: string[],
  attachment: Buffer,
  filename: string,
  routingContext: RingCentralRoutingContext
): Promise<RingCentralMessage> {
  return withRcClient(routingContext, async (platform, _extensionId, accountId) => {
    const formData = new FormData();

    const bodyParams = {
      to: recipients.map((recipient) => ({ phoneNumber: recipient })),
      faxResolution: "High",
      coverIndex: 0,
    };

    formData.append("json", Buffer.from(JSON.stringify(bodyParams)), {
      filename: "request.json",
      contentType: "application/json",
    });
    formData.append("attachment", Buffer.from(attachment), { filename });

    const endpoint = rcExtensionPath(routingContext.extensionId ?? "~", "fax", accountId);

    const resp = await retryWithBackoff(
      async () => {
        return platform.post(endpoint, formData);
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );
    const jsonObj = await resp.json();

    logger.info("FAX sent", {
      messageId: jsonObj.id,
      faxNumber: routingContext.faxNumber ? unredacted(routingContext.faxNumber) : null,
    });

    return jsonObj;
  });
}

export type RingCentralMessageStoreListPaging = {
  page: number;
  perPage: number;
  totalPages: number;
};

interface ReadMessageStoreRCOptions extends RingCentralRoutingContext {
  dateFrom: string;
  dateTo: string;
  direction: string;
  readStatus: RingCentralMessageStoreReadFilter;
  messageType: string[];
  page: number;
  perPage: number;
}

export async function readMessageStoreRC(
  options: ReadMessageStoreRCOptions
): Promise<{ records: RingCentralMessage[]; paging: RingCentralMessageStoreListPaging | null }> {
  const maxAttempts = 1;

  return withRcClient(options, async (platform, extensionId, accountId) => {
    const queryParams = {
      dateFrom: options.dateFrom,
      dateTo: options.dateTo,
      direction: options.direction,
      readStatus: options.readStatus,
      messageType: options.messageType,
      page: options.page,
      perPage: options.perPage,
    };

    const endpoint = rcExtensionPath(extensionId, "message-store", accountId);

    const resp = await retryWithBackoff(
      async () => {
        return platform.get(endpoint, queryParams);
      },
      maxAttempts,
      DEFAULT_BASE_DELAY
    );

    const jsonObj = (await resp.json()) as {
      records?: RingCentralMessage[];
      paging?: { page?: number; perPage?: number; totalPages?: number };
    };
    const records = jsonObj.records ?? [];
    const rawPaging = jsonObj.paging;
    const paging =
      rawPaging &&
      typeof rawPaging.page === "number" &&
      typeof rawPaging.perPage === "number" &&
      typeof rawPaging.totalPages === "number"
        ? {
            page: rawPaging.page,
            perPage: rawPaging.perPage,
            totalPages: rawPaging.totalPages,
          }
        : null;

    return { records, paging };
  });
}

export async function getFaxMessageAttachmentRC(
  messageId: string,
  attachmentId: string,
  routingContext: RingCentralRoutingContext
) {
  return withRcClient(routingContext, async (platform, extensionId, accountId) => {
    const endpoint = rcExtensionPath(extensionId, `message-store/${messageId}/content/${attachmentId}`, accountId);

    const resp = await retryWithBackoff(
      async () => {
        return platform.get(endpoint);
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );
    const buffer = await resp.arrayBuffer();
    const contentDisposition = resp.headers.get("content-disposition");
    let filename = "attachment.pdf";

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);

      if (match && match[1]) {
        filename = match[1];
      }
    }

    logger.info(`RC Fax message attachment saved to ${filename}`, {
      savedFilename: filename,
    });

    return new File([buffer], filename, { type: "application/pdf" });
  });
}

export async function markFaxMessageAsReadRC(messageId: string, routingContext: RingCentralRoutingContext) {
  return withRcClient(routingContext, async (platform, extensionId, accountId) => {
    const endpoint = rcExtensionPath(extensionId, `message-store/${messageId}`, accountId);

    return retryWithBackoff(
      async () => {
        return platform.put(endpoint, { readStatus: RingCentralMessageReadStatus.READ });
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );
  });
}

interface ForwardFaxRCOptions extends RingCentralRoutingContext {
  sendTime?: string;
  isRetry?: boolean;
}

export async function forwardFaxRC(
  messageId: string,
  recipients: string[],
  routingContext: ForwardFaxRCOptions
): Promise<RingCentralMessage> {
  return withRcClient(routingContext, async (platform, _extensionId, accountId) => {
    const bodyParams = {
      originalMessageId: messageId,
      to: recipients.map((recipient) => ({ phoneNumber: recipient })),
      ...(routingContext.sendTime && { sendTime: routingContext.sendTime }),
    };
    const endpoint = rcExtensionPath(routingContext.extensionId ?? "~", "fax", accountId);
    const resp = await retryWithBackoff(
      async () => {
        return platform.post(endpoint, bodyParams);
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );
    const jsonObj = await resp.json();

    if (routingContext.isRetry) {
      logger.info("FAX retry scheduled", { messageId: jsonObj.id });
    } else {
      logger.info("FAX forward scheduled", { messageId: jsonObj.id });
    }

    return jsonObj;
  });
}

export async function getFaxMessage(
  messageId: string,
  routingContext: RingCentralRoutingContext
): Promise<RingCentralMessage> {
  return withRcClient(routingContext, async (platform, extensionId, accountId) => {
    const endpoint = rcExtensionPath(extensionId, `message-store/${messageId}`, accountId);
    const resp = await retryWithBackoff(
      async () => {
        return platform.get(endpoint);
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );

    return resp.json();
  });
}

export async function getFaxMessages(
  messageIds: string[],
  routingContext: RingCentralRoutingContext
): Promise<RingCentralMessage[]> {
  if (messageIds.length === 0) {
    return [];
  }

  return withRcClient(routingContext, async (platform, extensionId, accountId) => {
    const endpoint = rcExtensionPath(extensionId, `message-store/${messageIds.join(",")}`, accountId);
    const resp = await retryWithBackoff(
      async () => {
        return platform.get(endpoint);
      },
      DEFAULT_MAX_ATTEMPTS,
      DEFAULT_BASE_DELAY
    );

    return parseMessageStoreBatchGetResponse(resp);
  });
}

async function handleMultipartResponse(response: Response) {
  const contentType = response.headers.get("content-type") || "";
  const boundary = contentType.split("boundary=")[1];

  if (!boundary) {
    throw new Error("Boundary not found in content-type");
  }

  const text = await response.text();
  const parts = text.split(`--${boundary}`).filter((part) => part.trim() !== "--" && part.trim() !== "");

  return parts.map((part) => {
    const [_headers, ...bodyParts] = part.split("\r\n\r\n");
    const body = bodyParts.join("\r\n\r\n").trim();

    try {
      return JSON.parse(body);
    } catch (error) {
      logger.error("Error parsing JSON part", { error });

      return { records: [] };
    }
  });
}

export async function resendFailedFax(
  messageId: string,
  recipients: string[],
  routingContext: RingCentralRoutingContext
): Promise<RingCentralMessage> {
  return forwardFaxRC(messageId, recipients, { ...routingContext, isRetry: true });
}
